import type { ProductHit } from "@/recourses/meilisearch/meiliSearch";
import { CURRENCY } from "@/recourses/constants";
import type { PulseBridgeProductCard } from "@/types/pulse-bridge";
import { calculatePercentageDiscount } from "./money";
import type { DiscountProductOrder, OrderProduct } from "@/services/ordersV2/type";
import type { IProduct } from "@/Pages/Product/type";
import { TYPE_DISCOUNT } from "@/services/checkout/type";

export const promofitOptions = ["normal", "promofit"];

export const baseProduct = {
  name: "Harina de Garbanzo - fithub x 250 g",
  brand: "Fithub",
  price: 5500,
  originalPrice: 5500,
  isNew: false,
  availabilityOnSaleOnline: true,
  imagen:
    "https://dexter-media-prod-co.s3.amazonaws.com/__sized__/products/79077149-crop-c0-5__0-5-255x255-90.jpg",
  brandSlug: "fithub",
  id: 1,
  quatityAvailable: 12,
  sku: "123456",
  slug: "harina-de-garbanzo-fithub-x-250-g",
  storeId: 1,
  storeType: "supermarket",
  isPromoted: false,
  bestSeller: false,
};

export const cartProduct: Cart = {
  storeId: "13",
  quantity: 1,
  product: {
    id: "1404",
    name: "Yogurt Griego con Stevia - fithub x 1 L",
    slug: "yogurt-griego-con-stevia-fithub-x-1-l",
    sku: 89028995,
    brand: {
      name: "fithub",
      slug: "fithub",
    },
    image: {
      alt: "Yogurt Griego con Stevia - fithub x 1 L",
      url: "https://dexter-media-staging-2-co.s3.amazonaws.com/__sized__/products/__filtered__/89028995__webp__-thumbnail-510x510-70.webp",
    },
  },
  details: {
    customLabel: "",
    customLabelBackgroundColor: "#000000",
    onSale: false,
    available: true,
    maxQtyToOrder: 23,
    averageWeight: 1,
    isWeightable: false,
  },
  price: {
    currency: "COP",
    discount: 0,
    finalPrice: 41500,
    originalPrice: 41500,
    membership: 0,
    perMeasureUnitDisplay: 0,
  },
};

export const arrayProducts = Array.from({ length: 16 }).map((_) => cartProduct);

const checkProductIsAvailable = (item: Cart) => {
  return (
    item.details.available &&
    (item.details.isWeightable && item.details.averageWeight
      ? item.details.maxQtyToOrder >= item.details.averageWeight
      : true)
  );
};

export const getMaxAllowedQuantity = (item: Cart) => {
  const allowOOS = item.details.allowOrderOutOfStock ?? false;
  return allowOOS
    ? item.details.maxQtyToOrder
    : (item.details.quantityAvailable ?? item.details.maxQtyToOrder);
};

export const checkProductHasStock = (item: Cart) => {
  const maxAllowed = getMaxAllowedQuantity(item);

  return (
    item.details.available &&
    item.quantity > 0 &&
    item.quantity <= maxAllowed &&
    checkProductIsAvailable(item)
  );
};

export const productHitToCart = (
  id: number,
  storeId: number,
  item: ProductHit | Record<string, any>
): Cart => {
  const discount = item?.discount;

  return {
    storeId: String(storeId),
    quantity: 1,
    product: {
      id: String(id),
      name: item.product_name,
      slug: item.product_slug,
      sku: +item.product_sku,
      brand: {
        name: item.product_brand,
        slug: item.product_brand_slug,
      },
      image: {
        alt: item.product_name,
        url: item.product_thumbnail_url,
      },
    },
    details: {
      averageWeight:
        item.product_is_weightable && item.product_average_weight !== null
          ? item.product_average_weight || 1
          : 1,
      isWeightable: item.product_is_weightable && item.product_average_weight !== null,
      onSale: item.product_availability_on_sale_online,
      available:
        item.product_availability_available_online &&
        (item.product_is_weightable && item.product_average_weight !== null
          ? item.product_availability_max_qty_to_order >= item.product_average_weight
          : true),
      maxQtyToOrder: item.product_availability_max_qty_to_order,
      customLabel: discount?.type ?? "",
      discountLabel: discount?.label ?? "",
      customLabelBackgroundColor: discount?.label_background_color ?? "",
      isPromoted: item.is_promoted === 1,
      isNew: item.is_new,
      measureTypeDisplayShort: item.product_measure_type_display_short,
      isImported: item.is_imported,
    },
    price: {
      currency: item.product_availability_currency,
      finalPrice: item.price_online_gross,
      originalPrice: item.price_online_undiscounted_gross,
      membership: item.price_online_membership_gross,
      discount: item.product_availability_discount_online,
    },
  };
};

export const recombeeToCart = productHitToCart;

export const pulseBridgeToCart = (item: PulseBridgeProductCard): Cart => {
  return productHitToCart(item.id, item.store_id, item);
};

export const cartToOrderProduct = (item: Cart, discount: DiscountProductOrder): OrderProduct => {
  return {
    ...discount,
    id: +item.product.id,
    product: {
      id: +item.product.id,
      brand: {
        id: +item.product.id,
        name: item?.product?.brand.name ?? "",
        slug: item.product.brand.slug ?? "",
      },
      image: item.product.image.url,
      name: item.product.name,
      sku: String(item?.product?.sku ?? ""),
      slug: item.product.slug,
      store_product_id: +(item.storeId ?? 0),
      measureTypeDisplayShort: item.details.measureTypeDisplayShort,
    },
    order_id: 0,
    quantity: item.quantity,
    averageWeight:
      item.details.isWeightable && item.details.averageWeight !== null ? item.details.averageWeight || 1 : 1,
    total: item.price.originalPrice,
    total_discounted: item.price.finalPrice,
    unit_price_gross: item.price.originalPrice?.toString(),
    unit_price_discounted_gross:
      item.price.finalPrice === item?.price?.originalPrice ? null : item.price.finalPrice?.toString(),
  };
};

export const orderProductToCartNotAvailable = (item: OrderProduct, storeId: string): Cart => {
  return {
    storeId,
    quantity: item.quantity,
    product: {
      id: String(item.product.store_product_id),
      name: item.product.name,
      slug: item.product.slug,
      sku: +item.product.sku,
      brand: {
        name: item.product.brand.name,
        slug: item.product.brand.slug,
      },
      image: {
        alt: item.product.name,
        url: item.product.image,
      },
    },
    details: {
      averageWeight: +(item.averageWeight || 1),
      isWeightable: +(item.averageWeight || 1) > 1,
      onSale: false,
      available: false,
      maxQtyToOrder: 0,
      customLabel: "",
      discountLabel: "",
      customLabelBackgroundColor: undefined,
      measureTypeDisplayShort: item.product.measureTypeDisplayShort ?? "",
    },
    price: {
      currency: CURRENCY ?? "COP",
      finalPrice: item.total_discounted || item.total,
      originalPrice: item.total,
      membership: 0,
      discount: 0,
      perMeasureUnitDisplay: 0,
    },
  };
};

export const getPercentageDiscount = ({
  unitPriceDiscountedGross,
  unitPriceGross,
}: {
  unitPriceDiscountedGross?: string | null;
  unitPriceGross?: string | null;
}) => {
  let percentage = 0;

  /* Calculate percentage */
  if (unitPriceDiscountedGross && unitPriceGross) {
    percentage = calculatePercentageDiscount({
      priceOriginal: parseFloat(unitPriceGross ?? "0"),
      priceDiscount: parseFloat(unitPriceDiscountedGross ?? "0"),
    });
  }

  return percentage;
};

export const oldCartToNewCart = (storeId: number, item: OldCart | IProduct): Cart => {
  return {
    storeId: String(storeId),
    quantity: "quantity" in item ? item.quantity : 1,
    product: {
      id: String(item.pk),
      name: item.details.name,
      slug: item.details.slug,
      sku: item.details.sku,
      brand: {
        name: item.details.brand.name,
        slug: item.details.brand.slug,
      },
      image: {
        alt: item.details.thumbnail?.alt ?? item.details.name,
        url: item.details.thumbnail.url,
      },
    },
    details: {
      averageWeight: item.details.averageWeight || 1,
      isWeightable: item.details.averageWeight !== undefined && item.details.averageWeight !== null,
      isNew: !!item.availability.isNew,
      onSale: item.availability.onSale,
      available:
        item.availability.available &&
        (item.details.averageWeight ? item.availability.maxQtyToOrder >= item.details.averageWeight : true),
      maxQtyToOrder: item.availability.maxQtyToOrder,
      customLabel: item?.customLabel || (item.availability.onSale ? TYPE_DISCOUNT.PROMOFIT : ""),
      discountLabel: item?.discountLabel ?? "",
      customLabelBackgroundColor: item?.customLabelBackgroundColor ?? "",
      isImported: item.isImported,
    },
    price: {
      currency: item?.availability?.price?.currency,
      finalPrice: item?.availability?.price?.gross ?? 0,
      originalPrice: item?.availability?.priceUndiscounted?.gross ?? 0,
      membership: item.availability.priceMembership?.gross ?? 0,
      discount: item.availability?.discount?.gross ?? item.availability?.discount ?? 0,
      perMeasureUnitDisplay:
        "pricePerMeasureUnitDisplay" in item.availability
          ? +(item.availability.pricePerMeasureUnitDisplay ?? 0)
          : undefined,
    },
  };
};

export const newCartToOldCart = (item: Cart): OldCart => {
  return {
    pk: String(item.product.id),
    quantity: item.quantity,
    customLabel: item.details.customLabel,
    discountLabel: item.details.discountLabel,
    customLabelBackgroundColor: item.details.customLabelBackgroundColor,
    availability: {
      onSale: item.details.onSale,
      available: checkProductIsAvailable(item),
      maxQtyToOrder: item.details.maxQtyToOrder,
      price: {
        gross: item.price.finalPrice,
        currency: item.price.currency,
      },
      discount: {
        gross: item.price.discount,
        currency: item.price.currency,
      },
      priceUndiscounted: {
        gross: item.price.originalPrice,
        currency: item.price.currency,
      },
      priceMembership: {
        gross: item.price.membership,
        currency: item.price.currency,
      },
    },
    details: {
      averageWeight: item.details.averageWeight || 1,
      name: item.product.name,
      slug: item.product.slug,
      avgRating: item.product?.avgRating,
      sku: +item.product.sku,
      containsAlcohol: item.product?.containsAlcohol ?? false,
      brand: {
        name: item.product.brand.name,
        slug: item.product.brand.slug,
      },
      thumbnail: {
        url: item.product.image.url,
      },
    },
  };
};

export const generateProductUrl = (slug: string, recommId?: string) => {
  return recommId ? `/p/${slug}?recommId=${recommId}` : `/p/${slug}`;
};

export const getDisplayBrand = (brand?: string): string => {
  if (!brand) return "Genérico";
  return brand.toLowerCase() !== "none" ? brand : "Genérico";
};
